import { useCallback } from "react";

import CodeEditor from "components/CodeEditor";
import PolicyTypeBadge from "components/PolicyTypeBadge";
import { External } from "components/icons/generated";
import Box from "ds/components/Box";
import LinkButtonIcon from "ds/components/ButtonIcon/LinkButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import useDetachPolicyFromIntentProject from "shared/IntentProject/useDetachPolicyFromIntentProject";
import { Action, IntentProject, IntentProjectPolicyAttachment } from "types/generated";
import { checkAllowedAction } from "utils/access";
import { getAttachedLabel } from "utils/labels";

import { showIntentProjectPoliciesDetachConfirmationModal } from "./DetachConfirmationModal";
import { GET_INTENT_PROJECT_POLICIES_REFETCH_QUERIES } from "./constants";
import styles from "./styles.module.css";

type IntentProjectPoliciesListItemProps = {
  policy: IntentProjectPolicyAttachment;
  intentProject: Pick<IntentProject, "allowedActions" | "labels">;
};

const IntentProjectPoliciesListItem = ({
  policy,
  intentProject,
}: IntentProjectPoliciesListItemProps) => {
  const isAutoAttached = policy.isAutoattached;
  const policyLink = `/policy/${policy.policyId}/`;
  const [isCollapsed, toggle] = useToggle(true);

  const { detachPolicy, loading } = useDetachPolicyFromIntentProject(
    GET_INTENT_PROJECT_POLICIES_REFETCH_QUERIES
  );

  const canDetachPolicy = checkAllowedAction(intentProject, Action.IntentProjectPolicyDetach);

  const handleDelete = useCallback(() => {
    showIntentProjectPoliciesDetachConfirmationModal({
      policyName: policy.policyName,
      onConfirm: () => {
        detachPolicy(policy.id);
      },
    });
  }, [policy.id, policy.policyName, detachPolicy]);

  return (
    <CardCollapsible
      ariaLevel={4}
      ariaLabel={policy.policyName}
      title={policy.policyName}
      size="small"
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={
        <LinkButtonIcon icon={External} to={policyLink} variant="ghost">
          Go to policy editor
        </LinkButtonIcon>
      }
      headerSuffix={
        <Box align="center" gap="medium" shrink="0">
          {isAutoAttached && (
            <Tag tag={getAttachedLabel(intentProject.labels, policy.policyLabels)} withAutoAttach />
          )}

          <PolicyTypeBadge type={policy.policyType} />

          <DropdownMenuEllipsis tooltip="Policy actions" buttonVariant="ghost">
            <DropdownMenuItem href={policyLink}>Go to policy editor</DropdownMenuItem>

            {!isAutoAttached && (
              <DropdownMenuItem
                loading={loading}
                danger
                onAction={handleDelete}
                isDisabled={!canDetachPolicy.result}
                tooltip={canDetachPolicy.message}
                tooltipPlacement="left"
              >
                Detach policy
              </DropdownMenuItem>
            )}
          </DropdownMenuEllipsis>
        </Box>
      }
    >
      <CodeEditor
        className={styles.codeEditor}
        body={policy.policyBody}
        language="rego"
        autoHeight
        maxAutoHeight={460}
        readOnly
      />
    </CardCollapsible>
  );
};

export default IntentProjectPoliciesListItem;
